// @ts-nocheck
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { ConfigurationType } from "@insite/client-framework/Services/ProductServiceV2";
import {
    chain,
    LoadRealTimeInventory,
    LoadRealTimePricing,
    SetConfigurationCompleted,
} from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/UpdateConfigurationSelection";
import { ConfigurationModel } from "@insite/client-framework/Types/ApiModels";
import {
    filterConfiguration,
    FilterConfigurationApiParameter,
    FilteredConfigurationAvailabilityModel,
    getConfigurationAvailability,
} from "@store/Framework/Services/DynamicConfiguratorService";
import { addToChainBefore, Handler, replaceInChain } from "@toolkit/Modules/client-framework/HandlerCreator";

interface Parameter {
    configSectionId?: string;
    sectionOptionId?: string;
    productId: string;
}

interface Props {
    configurationSelection: SafeDictionary<string>;
    configurationCompleted: boolean;
    productInfo?: ProductInfo;
    filteredConfigurable?: {
        id: string;
        maxBtu: string;
        currentBtu: string;
        voltage: string;
        availability: FilteredConfigurationAvailabilityModel | undefined;
        hasIncompatibleSpecialUnitRestriction?: boolean;
        specialUnitRestrictionMessageKey?: string;
        specialUnitRestrictionBySectionOptionId?: SafeDictionary<boolean>;
    };
}

type HandlerType = Handler<Parameter, Props>;

const swapEmptyGuid = (obj: SafeDictionary<string>): SafeDictionary<string> => {
    const newObj = { ...obj };
    for (const key in newObj) {
        if (newObj[key] === "00000000-0000-0000-0000-000000000000") {
            newObj[key] = "";
        }
    }
    return newObj;
};

export const FilterConfiguration: HandlerType = async props => {
    const productDetail = props.getState().pages.productDetails;
    const totalSelections = productDetail.configurationSelection
        ? Object.keys(productDetail.configurationSelection).length
        : 0;
    if (totalSelections > 0) {
        if (!props.getState().pages.productDetails.lastProductPath) {
            return;
        }
        const fullProduct = JSON.parse(
            JSON.stringify(props.getState().data.products.byId[productDetail.selectedProductId!]),
        );
        if (
            !fullProduct.detail ||
            !fullProduct.detail.configuration ||
            fullProduct.configurationType !== ConfigurationType.Standard
        ) {
            return;
        }
        const payload = {
            KitId: fullProduct.productNumber,
            CurrentBtu: "0",
            MaxBtu: "0",
            Voltage: "",
            ConfigurationSelection: props.configurationSelection,
        } as FilterConfigurationApiParameter;
        const filteredConfiguration = await filterConfiguration(payload);
        if (!filteredConfiguration.successful) {
            throw new Error(filteredConfiguration.errorMessage);
        }
        fullProduct.detail.configuration = filteredConfiguration.result.configuration as ConfigurationModel;
        props.dispatch({
            type: "Data/Products/CompleteLoadProduct",
            model: fullProduct,
        });
        const filteredConfigurable = {
            id: fullProduct.id,
            maxBtu: filteredConfiguration.result.maxBtu,
            currentBtu: filteredConfiguration.result.currentBtu,
            voltage: filteredConfiguration.result.voltage,
            availability: undefined,
            hasIncompatibleSpecialUnitRestriction: filteredConfiguration.result.hasIncompatibleSpecialUnitRestriction,
            specialUnitRestrictionMessageKey: filteredConfiguration.result.specialUnitRestrictionMessageKey,
            specialUnitRestrictionBySectionOptionId: filteredConfiguration.result.specialUnitRestrictionBySectionOptionId,
        };
        props.dispatch({
            type: "Store/Data/FilteredConfigurable/SetFilteredConfigurable",
            filteredConfigurable,
        });
        if (filteredConfiguration.result.configurationSelection) {
            props.filteredConfigurable = filteredConfigurable;
            props.configurationSelection = swapEmptyGuid(filteredConfiguration.result.configurationSelection);
        }
    }
};

export const DoFilterAvailability: HandlerType = async props => {
    const state = props.getState();
    const filteredConfigurable = props.filteredConfigurable;
    const id = filteredConfigurable?.id;
    if (!id) {
        return;
    }
    const configurationSelection = props.configurationSelection;
    const configSections = state.data.products.byId[id].detail?.configuration?.configSections;
    if (!configSections) {
        return;
    }
    const productIds: string[] = Object.keys(configurationSelection).reduce<string[]>((acc, key) => {
        const selectedOptionId = configurationSelection[key];
        if (selectedOptionId) {
            const section = configSections.find(section => section.id === key);
            if (section) {
                const option = section.sectionOptions?.find(option => option.id === selectedOptionId);
                if (option && option.productId) {
                    acc.push(option.productId);
                }
            }
        }
        return acc;
    }, []);
    const payload = {
        ParentId: id,
        ChildrenIds: productIds,
    };
    const configurationAvailability = await getConfigurationAvailability(payload);
    if (!configurationAvailability.successful) {
        throw new Error(configurationAvailability.errorMessage);
    }
    const result = {
        ...filteredConfigurable,
        availability: configurationAvailability.result,
    };
    props.dispatch({
        type: "Store/Data/FilteredConfigurable/SetFilteredConfigurable",
        filteredConfigurable: result,
    });
    props.filteredConfigurable = result;
};

addToChainBefore(chain, SetConfigurationCompleted, FilterConfiguration);
addToChainBefore(chain, LoadRealTimePricing, DoFilterAvailability);

// Disable RealTime features that are not used on DCNE
replaceInChain(chain, LoadRealTimeInventory, () => {});
